import React, { useMemo, useState } from 'react';
import { useGlobalPositions, useGlobalData } from '../hooks/useGlobalHooks';
import { usePositionStore } from '../contexts/LTPStore/usePositionsStore';
import { useWebSocketStatusStore } from '../contexts/LTPStore/useWebSocketStatusStore';
import WebSocketManager from '../contexts/websocketManager';
import toast from "react-hot-toast";
import apiClient from '../utils/apiClient';
import PositionsPanel from './DashboardNewComponents/PositionsPanel';
import { AlertTriangle } from 'lucide-react';
import server from '../utils/serverConfig';
import ExitSuccessModal from './ExitSuccessModal';
import ExitPayloadModal from './ExitPayloadModal';
/**
 * RawPositionsView
 * Shows raw positions data in a searchable, sortable table.
 * No action buttons are included (read-only view).
 */
export function ExitRawPositionsView({ clientData }) {
  const { loading } = useGlobalPositions();
  const positions = usePositionStore((state) => state.positions);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // console.log("Client Data---------", clientData);
  // Build dynamic column list from keys present in positions (exclude last_updated)
  const allKeys = useMemo(() => {
    const keys = new Set();
    if (positions && positions.length > 0) {
      positions.forEach(p => {
        if (p && typeof p === 'object') {
          Object.keys(p).forEach(k => {
            // exclude noisy/unneeded columns
            if (k === 'last_updated' || k === 'id') return; // exclude
            keys.add(k);
          });
        }
      });
    }

    // console.log("positions-----RAW ONE TTT---", positions);
    // Prefer an order for common fields if present
    const preferred = ['symbol', 'client_id', 'clientId', 'side', 'net_qty', 'qty', 'avg_price', 'net_avg', 'ltp', 'current_ltp', 'strategy', 'pl', 'pnl'];
    const ordered = [];
    preferred.forEach(k => { if (keys.has(k)) { ordered.push(k); keys.delete(k); } });
    const rest = Array.from(keys).sort();
    return [...ordered, ...rest];
  }, [positions]);


  const [showExitModal, setShowExitModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [openStrategy, setOpenStrategy] = useState({});

  const openExitModal = (pos) => {
    setSelectedPosition(pos);
    setShowExitModal(true);
  };



  const [isLoading, setIsLoading] = useState(false);


  const clientBrokerMap = useMemo(() => {
    const map = {};
    if (clientData && Array.isArray(clientData)) {
      clientData.forEach(item => {
        // Prevent overwriting valid/active broker with zero-multiplier duplicates
        if ((item.totalMul && item.totalMul > 0) || !map[item.clientId]) {
          map[item.clientId] = item.broker;
        }
      });
    }
    return map;
  }, [clientData]);






  const [openLabel, setOpenLabel] = useState({});
  const [openAsset, setOpenAsset] = useState({});
  const [openSymbol, setOpenSymbol] = useState({});
  const [selectedMultiple, setSelectedMultiple] = useState([]);
  const [ExitResponse, setExitResponse] = useState([]);
  const [showExitMultipleModal, setShowExitMultipleModal] = useState(false);



  const openExitModalForMultiple = (payloadList) => {
    // console.log("EXITING MULTIPLE POSITIONS:", payloadList);
    setSelectedMultiple(payloadList);   // <-- you must have this state
    setShowExitMultipleModal(true);     // <-- and this state as well
  };

  // console.log("SELECTED MULTIPLE ITEMS---------", selectedMultiple);

  const nestedPositions = useMemo(() => {
    if (!positions || positions.length === 0 || !clientData) return [];

    // Map clientId → label from clientData
    const labelMap = {};
    const pledgePlusMap = {};
    clientData.forEach(c => {
      const id = c.clientId || c.client_id;
      if (!id) return;
      
      // Only set label if truthy, to prevent undefined duplicates overwriting valid labels
      if (c.label) {
        labelMap[id] = c.label;
      }
      
      const isPP = c.is_pledge_plus === true || String(c.is_pledge_plus).toLowerCase() === 'true';
      const isPPMini = c.is_pledgeplus_mini === true || String(c.is_pledgeplus_mini).toLowerCase() === 'true';
      if (isPP || isPPMini) {
        pledgePlusMap[id] = true;
      }
    });

    const group = {};

    // Index categories
    const indexList = ["NIFTY", "BANKNIFTY", "FINNIFTY", "SENSEX", "BSE"];

    positions.forEach(pos => {
      const cid = pos.client_id || pos.clientId;
      const positionStrat = pos.strategy || "N/A";
      const isNA = positionStrat === "N/A" || positionStrat === "N";

      let label = labelMap[cid] || "UNLABELED";
      if (isNA) {
        label = "UNLABELED";
      }

      const sym = pos.symbol || "";
      const isFut = sym.toUpperCase().includes("FUT");
      let asset = "Stock Options"; // default

      // 2. Asset Type Detection
      if (sym.startsWith("MCX:")) {
        asset = "MCX";
      } else if (indexList.some(idx => sym.includes(idx))) {
        asset = isFut ? "Index Futures" : "Index Options";
      } else {
        asset = isFut ? "Stock Futures" : "Stock Options";
      }

      if (pledgePlusMap[cid] && (asset === "MCX" || isFut)) {
        if (!isNA && positionStrat !== "ZEROFOLIO") {
          label = "PLEDGEPLUS";
        }
      }

      // 1. Group by label
      if (!group[label]) group[label] = {};

      // Ensure asset group
      if (!group[label][asset]) group[label][asset] = {};

      // 3. Group by symbol
      if (!group[label][asset][sym]) group[label][asset][sym] = {};

      // 4. Group by strategy
      const strat = pos.strategy || "NO_STRATEGY";
      if (!group[label][asset][sym][strat]) group[label][asset][sym][strat] = [];

      group[label][asset][sym][strat].push(pos);
    });

    // Final array version
    return Object.keys(group).map(label => ({
      label,
      assets: group[label] // asset → symbol → strategy → array[]
    }));
  }, [positions, clientData]);


  const processed = useMemo(() => {
    if (!positions || positions.length === 0) return [];

    const q = searchQuery.trim().toLowerCase();

    let filtered = positions.filter(p => {
      if (!q) return true;
      // check a few likely fields first
      const fieldsToCheck = [p.symbol, p.client_id || p.clientId, p.strategy, String(p.net_qty || p.qty || ''), String(p.pl || p.pnl || '')];
      const match = fieldsToCheck.some(f => String(f || '').toLowerCase().includes(q));
      if (match) return true;
      // fallback: search full JSON
      return JSON.stringify(p).toLowerCase().includes(q);
    });

    // Sorting - improved to handle numbers with commas/currency, objects (stringified), and nulls
    if (sortConfig.key) {
      const key = sortConfig.key;

      const parseNumberLike = (v) => {
        if (v === null || typeof v === 'undefined') return NaN;
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        // If object, stringify first
        let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        // Remove non-numeric chars except dot and minus
        const cleaned = s.replace(/[^0-9.-]+/g, '');
        const n = parseFloat(cleaned);
        return Number.isFinite(n) ? n : NaN;
      };

      filtered.sort((a, b) => {
        const aRaw = a[key];
        const bRaw = b[key];

        // Handle undefined/null: treat as empty string, push to end
        const aIsNull = aRaw === null || typeof aRaw === 'undefined';
        const bIsNull = bRaw === null || typeof bRaw === 'undefined';
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bIsNull) return sortConfig.direction === 'asc' ? -1 : 1;

        // Try numeric compare first
        const aNum = parseNumberLike(aRaw);
        const bNum = parseNumberLike(bRaw);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        // Fallback to string compare (stringify objects so UI matches sort)
        const aStr = (typeof aRaw === 'object') ? JSON.stringify(aRaw) : String(aRaw);
        const bStr = (typeof bRaw === 'object') ? JSON.stringify(bRaw) : String(bRaw);
        const aLower = aStr.toLowerCase();
        const bLower = bStr.toLowerCase();
        if (aLower < bLower) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aLower > bLower) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [positions, searchQuery, sortConfig]);


  // Stats derived from positions
  const stats = useMemo(() => {
    const total = positions ? positions.length : 0;
    let totalPnl = 0;
    const clients = new Set();
    if (positions && positions.length) {
      positions.forEach(p => {
        const pnl = parseFloat(p.pl ?? p.pnl ?? 0) || 0;
        totalPnl += pnl;
        if (p.client_id) clients.add(p.client_id);
        if (p.clientId) clients.add(p.clientId);
      });
    }
    return { total, totalPnl, uniqueClients: clients.size };
  }, [positions]);




  const [showExitSuccessModal, setShowExitSuccessModal] = useState(false);
  const [exitSuccessOrders, setExitSuccessOrders] = useState([]);


  const handleExitAll = async (selectedMultiple, qtys = {}, types = {}) => {
    if (!selectedMultiple || selectedMultiple.length === 0) return;

    // Helper to apply quantity and structure payload consistently
    const processItems = (items) => items.map(p => {
      const key = `${p.client_id || p.clientId}|${p.symbol}|${p.strategy}`;
      let finalQty = Math.abs(p.net_qty ?? p.qty ?? p.netQty ?? 0);

      if (qtys[key]) {
        finalQty = parseFloat(qtys[key]);
      }

      let finalType = types[key] || "MARKET";

      // Return properties expected by backend
      // Note: ExitRawPositions might have slightly different prop names than RawPositions, 
      // so we ensure we pass what the backend expects (which seems to be the object itself + modified qty)
      return {
        ...p,
        netQty: finalQty, // Ensure netQty is updated
        qty: finalQty,    // Update qty just in case
        type: finalType
      };
    });

    const xtsPositions = [];
    const xtsAnuragPositions = [];
    const normalPositions = [];

    selectedMultiple.forEach(p => {
      const broker = clientBrokerMap[p.clientId || p.client_id];
      if (broker === 'dhan_xts' || broker === 'dhan_ss') {
        xtsPositions.push(p);
      } else if (broker === 'dhan_xts_anurag' || broker === 'dhan_ss_anurag') {
        xtsAnuragPositions.push(p);
      } else {
        normalPositions.push(p);
      }
    });

    const payloadXTS = processItems(xtsPositions);
    const payloadXTSAnurag = processItems(xtsAnuragPositions);
    const payloadNormal = processItems(normalPositions);

    console.log("EXIT MULTIPLE - XTS:", payloadXTS);
    console.log("EXIT MULTIPLE - XTS ANURAG:", payloadXTSAnurag);
    console.log("EXIT MULTIPLE - NORMAL:", payloadNormal);

    setIsLoading(true);

    const promises = [];

    console.log("DEBUG: Sending to Godseye:", `${server.godseye.baseUrl}exit_position`);
    console.log("DEBUG: Payload Normal:", JSON.stringify(payloadNormal));
    
    // 1. Request for Normal
    if (payloadNormal.length > 0) {
      promises.push(
        apiClient.post(`${server.godseye.baseUrl}exit_position`, payloadNormal, {
          headers: { "Content-Type": "application/json" }
        }).then(res => {
          console.log("DEBUG: Godseye response successful", res.data);
          return { type: 'normal', data: res.data };
        }).catch(err => {
          console.error("DEBUG: Godseye request failed", err);
          throw err;
        })
      );
    }

    // 2. Request for XTS
    if (payloadXTS.length > 0) {
      promises.push(
        apiClient.post(`${server.ss_dhan.baseUrl}exit-position`, payloadXTS, {
          headers: { "Content-Type": "application/json" }
        }).then(res => ({ type: 'xts', data: res.data }))
      );
    }

    // 3. Request for XTS Anurag
    if (payloadXTSAnurag.length > 0) {
      promises.push(
        apiClient.post(`${server.ss_dhan_anurag.baseUrl}exit-position`, payloadXTSAnurag, {
          headers: { "Content-Type": "application/json" }
        }).then(res => ({ type: 'xts_anurag', data: res.data }))
      );
    }

    try {
      const results = await Promise.allSettled(promises);
      console.log("results", results);
      let allFormattedOrders = [];
      let failureCount = 0;

      results.forEach(result => {
        if (result.status === 'fulfilled') {
          const resData = result.value.data;
          // Check for status 200 (or "success")
          if (resData?.status === 200 || resData?.status === 'success') {

            // Handle structure variations:
            // 1. resData.response is Array -> [ {responses: [...]}, {responses: [...]} ]
            // 2. resData.response.responses (Old single broker)
            // 3. resData.responses (New XTS direct)

            let rawResponses = [];
            const backendResponse = resData?.response;

            if (Array.isArray(backendResponse)) {
              // New logic: List of results from multiple brokers (e.g. [XTS_Response, Zerodha_Response])
              backendResponse.forEach(item => {
                if (!item) return;

                // If the item has a 'responses' key (common pattern)
                if (item.responses && Array.isArray(item.responses)) {
                  rawResponses.push(...item.responses);
                }
                // Or if the item itself is the list of orders
                else if (Array.isArray(item)) {
                  rawResponses.push(...item);
                }
              });
            } else if (backendResponse?.responses && Array.isArray(backendResponse.responses)) {
              // Old logic: Single object wrapper
              rawResponses = backendResponse.responses;
            } else if (resData?.responses && Array.isArray(resData.responses)) {
              // Fallback: Direct property on root
              rawResponses = resData.responses;
            }

            // Flatten in case of nested arrays and safeguard
            rawResponses = (rawResponses || []).flat();
            console.log("Raw Responses", rawResponses);
            const formatted = rawResponses.map((item) => ({
              clientId: item.client_id,
              symbol: item.symbol,
              broker: item.broker,
              // Handle ID variations
              orderId: item.response?.result?.AppOrderID || item.response?.order_id || item.response?.id,
              apiStatus: item.response?.order_status || item.status || "Sent",
              status: "Waiting",
              error: item.error || null
            }));
            allFormattedOrders = [...allFormattedOrders, ...formatted];
          } else {
            console.error("Exit API returned non-200 status:", resData);
            failureCount++;
          }
        } else {
          console.error("Exit Request Failed:", result.reason);
          failureCount++;
        }
      });

      console.log("Formatted Order", allFormattedOrders);

      if (allFormattedOrders.length > 0) {
        toast.success(`Exit Initiated for ${allFormattedOrders.length} positions.`);
        setExitSuccessOrders(allFormattedOrders);
        setShowExitMultipleModal(false);
        setSelectedMultiple([]);
        setShowExitSuccessModal(true);
      }

      if (failureCount > 0) {
        toast.error(`${failureCount} batch request(s) return error.`);
      }

    } catch (error) {
      console.error("Critical error in exit all:", error);
      toast.error("Critical error executing exit requests");
    } finally {
      setIsLoading(false);
    }
  };






  const prettifyKey = (k) => {
    if (!k) return '';
    // replace underscores and camelCase boundaries with spaces, then Title Case
    const spaced = k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
    return spaced.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }

    return (
      <svg className="w-3 h-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const formatCurrency = (v) => {
    const num = parseFloat(v);
    if (Number.isNaN(num)) return String(v ?? '-');
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
    } catch (e) {
      return num.toFixed(2);
    }
  };

  const normalizeSide = (s) => {
    if (s === 1 || s === '1' || String(s).toLowerCase().startsWith('b')) return 'BUY';
    if (s === 2 || s === -1 || s === '2' || String(s).toLowerCase().startsWith('s')) return 'SELL';
    return String(s || '')?.toUpperCase();
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  if (loading) {
    return (
      <div className="bg-[#1a1d29] p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg className="inline-block animate-spin h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-400 mt-4">Loading positions...</p>
        </div>
      </div>
    );
  }

  const isEmpty = !processed || processed.length === 0;
  // console.log("processed DATA of positions-----", processed);
  return (
    <div className="bg-[#1a1d29] h-full overflow-hidden flex flex-col">

      <div className="flex items-center justify-between mb-3 px-4 pt-2">
        <div className="flex items-center gap-3">

          <div className="text-sm text-gray-400">Showing {processed.length} / {positions.length} positions</div>
          <div className="text-sm text-gray-400 ml-4">Unique Clients: {stats.uniqueClients}</div>
        </div>


      </div>


      <PositionsPanel
        nestedPositions={nestedPositions}
        openLabel={openLabel}
        setOpenLabel={setOpenLabel}
        openAsset={openAsset}
        setOpenAsset={setOpenAsset}
        openSymbol={openSymbol}
        setOpenSymbol={setOpenSymbol}
        openExitModal={openExitModal}
        openExitModalForMultiple={openExitModalForMultiple}
        clientBrokerMap={clientBrokerMap}
        openStrategy={openStrategy}
        setOpenStrategy={setOpenStrategy}
      />


      <ExitPayloadModal
        isOpen={showExitMultipleModal}
        onClose={() => setShowExitMultipleModal(false)}
        positions={selectedMultiple}
        onConfirm={(finalQtys, finalTypes) => handleExitAll(selectedMultiple, finalQtys, finalTypes)}
        onRemove={(p) => {
          // Remove from selectedMultiple - Fix: strict comparison
          setSelectedMultiple(prev => {
            return prev.filter(item => {
              // Compare strictly
              const itemCid = item.clientId || item.client_id;
              const pCid = p.clientId || p.client_id;

              const itemSym = item.symbol;
              const pSym = p.symbol;

              const itemStrat = item.strategy;
              const pStrat = p.strategy;

              const isMatch = (itemCid === pCid) && (itemSym === pSym) && (itemStrat === pStrat);
              return !isMatch; // Keep if NOT match
            });
          });
        }}
        isLoading={isLoading}
      />



      {/* EXIT SUCCESS MODAL */}

      {/* EXIT SUCCESS MODAL */}
      <ExitSuccessModal
        isOpen={showExitSuccessModal}
        onClose={() => setShowExitSuccessModal(false)}
        orders={exitSuccessOrders}
      />




    </div>
  );
}

export default ExitRawPositionsView;
